/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Standard Cryptographic Utility for PHARMA TRACE (Fase 3)
 * Uses native Web Crypto API (SHA-256) for audit integrity and tamper detection.
 */

export async function computeSha256(data: string | object): Promise<string> {
  const text = typeof data === 'string' ? data : JSON.stringify(data);
  const encoder = new TextEncoder();
  const buffer = encoder.encode(text);
  
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  // Fallback for non-browser environments if Web Crypto is unavailable
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return `sha256_fallback_${Math.abs(hash).toString(16).padStart(16, '0')}`;
}

export async function verifyBackupIntegrity(backupData: {
  id: string;
  data: any;
  hashSha256?: string;
}): Promise<{ valid: boolean; calculatedHash: string; originalHash?: string }> {
  if (!backupData || !backupData.data) {
    return { valid: false, calculatedHash: '' };
  }
  
  const calculatedHash = await computeSha256(backupData.data);
  
  if (!backupData.hashSha256) {
    return {
      valid: true, // Legacy backup without hash
      calculatedHash,
      originalHash: undefined
    };
  }
  
  const valid = backupData.hashSha256.toLowerCase() === calculatedHash.toLowerCase();
  return {
    valid,
    calculatedHash,
    originalHash: backupData.hashSha256
  };
}
